import React, { useEffect, useState } from "react";
import { clearPersistedPageIndexes } from "../hooks/usePersistedState";
import { Link, useNavigate } from "react-router-dom";
import { useAuthUser, useIsAuthenticated, useSignOut } from "react-auth-kit";
import { Avatar, Box, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import {
  AccountCircle,
  Business,
  Logout,
  MiscellaneousServices,
  MoreVert,
  Notifications,
} from "@mui/icons-material";
import axios from "axios";
import Sidebar from "./Sidebar";
import ApplicationLogoHorizontal from "./common/ApplicationLogoHorizontal";
import ModalBackground from "./common/ModalBackground";
import localize from "../common/localize";
import DeviceSetup from "../pages/deviceSetup/DeviceSetup";
import useGlobalContext from "../hooks/useGlobalContext";
import ErrorMessage from "./common/ErrorMessage";
import { AUTH_ENDPOINT, NOTIFICATION_ENDPOINT } from "../services/config";
import SignOut from "./common/SignOut";
import CompanyData from "../pages/company/CompanyData";
import GeneralNotificationArea from "./common/GeneralNotificationArea";
import NotificationPane from "./common/NotificationPane";
import NotificationBadge from "./common/NotificationBadge";
import UserProfile from "../pages/userProfile/UserProfile";
import { getClearNotificationPreferences } from "../common/EntitiesManagement";
import EventSearcherHeader from "./common/EventSearcherHeader";

export default function Header() {
  const [notificationCount, setNotificationCount] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const { createEntity, updateEntity, DEVICE_SETUP_API, config } =
    useGlobalContext();
  const [newSetup, setNewSetup] = useState();
  const [showModal, setShowModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [showCompanyDataModal, setShowCompanyDataModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [notificationPreferences, setNotificationPreferences] = useState(
    getClearNotificationPreferences()
  );
  const [showSucceededModal, setShowSucceededModal] = useState(false);

  const auth = useAuthUser();
  const isAuthenticated = useIsAuthenticated();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const userData = auth();

  const validateOperationResult = (result) => {
    setShowModal(false);
    setShowUserProfile(false);

    if (!result.ok && !result?.data?.Ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    setShowSucceededModal(true);
  };

  const requestLogOut = async () => {
    await axios.put(
      `${AUTH_ENDPOINT}/logout/${userData?.SessionId}`,
      {},
      config
    );
  };

  const handleSetupSave = async () => {
    if (newSetup) {
      if (newSetup.Id && newSetup.Id > 0) {
        const updateResult = await updateEntity(
          `${DEVICE_SETUP_API}/${newSetup.Id}`,
          newSetup,
          config
        );

        validateOperationResult(updateResult);
        return;
      }

      const createResult = await createEntity(
        DEVICE_SETUP_API,
        newSetup,
        config
      );

      validateOperationResult(createResult);
    }
  };

  const getUserRole = () => {
    if (userData?.UserRoles?.length > 0) {
      return userData.UserRoles[0].Name;
    }

    return "";
  };

  const disableSaveChangesForDeviceSetup = () =>
    !newSetup?.ApplicationBaseUrl ||
    newSetup?.ApplicationBaseUrl.replaceAll(" ", "").length <= 0 ||
    !newSetup.SignInBaseUrl ||
    newSetup?.SignInBaseUrl.replaceAll(" ", "").length <= 0;

  const saveNotificationPreferences = async (preferences) => {
    try {
      let result = undefined;

      if (preferences?.id > 0) {
        result = await axios.put(
          `${NOTIFICATION_ENDPOINT}/${userData.UserName}/preference/${preferences.id}`,
          preferences,
          config
        );
      } else {
        result = await axios.post(
          `${NOTIFICATION_ENDPOINT}/${userData.UserName}/preference`,
          preferences,
          config
        );
      }

      validateOperationResult(result);
    } catch (Exception) {
      console.error("Error saving notification preferences", Exception);
      setShowUserProfile(false);
      setErrorMessage(Exception.message);
      setShowErrorModal(true);
    } finally {
      setShowUserProfile(false);
    }
  };

  return isAuthenticated() ? (
    <div className="w-full flex flex-col">
      <nav className="py-1 main_application_background_color menu_foreground_color relative flex flex-wrap items-center bg-base-200">
        {showErrorModal && (
          <ErrorMessage
            message={errorMessage}
            title={localize.error_unexpected}
            showModal={showErrorModal}
            setShowModal={setShowErrorModal}
          />
        )}

        {showSucceededModal && (
          <ModalBackground
            title={localize.app_name}
            showModal={showSucceededModal}
            setShowModal={setShowSucceededModal}
            onClickToSubmit={() => {
              setShowSucceededModal(false);
            }}
            okButtonText={localize.ok}
            hideCancelButton
            Component={
              <Box className="p-2">
                <div className="main_application_foreground_color w-full px-2">
                  <p>{localize.information_saved_successfully}</p>
                </div>
              </Box>
            }
          />
        )}

        {showUserProfile && (
          <ModalBackground
            isDrawerMode
            title={localize.tooltip_user}
            showModal={showUserProfile}
            setShowModal={setShowUserProfile}
            onClickToCancel={() => {
              setShowUserProfile(false);
            }}
            cancelButtonText={localize.close}
            onClickToSubmit={() => {
              saveNotificationPreferences(notificationPreferences);
              setShowUserProfile(false);
            }}
            Component={
              <UserProfile
                userData={userData}
                notificationPreferences={notificationPreferences}
                setNotificationPreferences={setNotificationPreferences}
              />
            }
            customSecondaryAction={{
              name: localize.sign_out,
              action: () => {
                setShowUserProfile(false);
                setShowSignOutModal(true);
              },
            }}
          />
        )}

        {showModal && (
          <ModalBackground
            hasRequiredValues
            isDrawerMode
            title={localize.device_setup}
            showModal={showModal}
            setShowModal={setShowModal}
            onClickToCancel={() => {
              setShowModal(false);
            }}
            onClickToSubmit={() => {
              handleSetupSave();
            }}
            Component={
              <DeviceSetup
                newSetup={newSetup}
                setNewSetup={setNewSetup}
                showModal={showModal}
              />
            }
            validators={disableSaveChangesForDeviceSetup()}
          />
        )}

        {showCompanyDataModal && (
          <CompanyData
            showCompanyModal={showCompanyDataModal}
            setShowCompanyModal={setShowCompanyDataModal}
          />
        )}

        {showSignOutModal && (
          <ModalBackground
            okButtonText={localize.sign_out}
            title={localize.app_name}
            showModal={showSignOutModal}
            setShowModal={setShowSignOutModal}
            onClickToCancel={() => {
              setShowSignOutModal(false);
            }}
            onClickToSubmit={() => {
              setShowSignOutModal(false);
              navigate("/");
              requestLogOut();
              clearPersistedPageIndexes();
              signOut();
            }}
            Component={<SignOut />}
          />
        )}

        <div className=" w-full mx-4 flex items-center justify-between">
          <div className="items-center gap-5 flex justify-start">
            <div>
              <Sidebar />
            </div>
            <Tooltip title={localize.home}>
              <Link
                className="font-bold text-black menu_foreground_color"
                to="/"
                style={{ marginBottom: "10px" }}
              >
                <ApplicationLogoHorizontal height={20} width={110} />
              </Link>
            </Tooltip>
          </div>
          <div className="header">
            <div className="menu-icon" onClick={toggleMenu}>
              <MoreVert className="text-xs" />
            </div>

            <ul className={`menu ${isMenuOpen ? "show" : ""}`}>
              <li>
                <EventSearcherHeader />
              </li>
              <li>
                <Box
                  className="h-full"
                  sx={{
                    userSelect: "none",
                    "&:focus": {
                      backgroundColor: "transparent",
                      outline: "none",
                    },
                    "&:active": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <a href="#">
                    <Tooltip title={localize.tooltip_device_settings}>
                      <button
                        onClick={() => {
                          setShowModal(true);
                        }}
                      >
                        <Box className="flex gap-2 items-center">
                          <MiscellaneousServices className="text-2xl" />
                          <span className="menu-text">
                            {localize.tooltip_device_settings}
                          </span>
                        </Box>
                      </button>
                    </Tooltip>
                  </a>
                </Box>
              </li>
              <li>
                <Box
                  className="h-full"
                  sx={{
                    userSelect: "none",
                    "&:focus": {
                      backgroundColor: "transparent",
                      outline: "none",
                    },
                    "&:active": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <a href="#">
                    <Tooltip title={localize.company_information}>
                      <button
                        onClick={() => {
                          setShowCompanyDataModal(true);
                        }}
                      >
                        <Box className="flex gap-2 items-center">
                          <Business className="text-2xl" />
                          <span className="menu-text">
                            {localize.company_information}
                          </span>
                        </Box>
                      </button>
                    </Tooltip>
                  </a>
                </Box>
              </li>
              <li>
                <Box
                  className="h-full"
                  sx={{
                    userSelect: "none",
                    "&:focus": {
                      backgroundColor: "transparent",
                      outline: "none",
                    },
                    "&:active": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <a href="#">
                    <Tooltip title={localize.notifications}>
                      <button
                        onClick={() => {
                          setShowNotifications(true);
                        }}
                      >
                        <Box className="flex gap-2 items-center">
                          <Notifications className="text-2xl" />

                          <span className="menu-text">
                            {localize.notifications}
                          </span>

                          {notificationCount > 0 && (
                            <NotificationBadge
                              notificationsCount={notificationCount}
                            />
                          )}
                        </Box>
                      </button>
                    </Tooltip>
                  </a>
                </Box>
              </li>
              <li>
                <Box
                  className="h-full"
                  sx={{
                    userSelect: "none",
                    "&:focus": {
                      backgroundColor: "transparent",
                      outline: "none",
                    },
                    "&:active": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <Tooltip
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: "#fff",
                          color: "text.primary",
                          boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                          borderRadius: 2,
                          p: 1.5,
                          "& .MuiTooltip-arrow": { color: "#fff" },
                        },
                      },
                    }}
                    title={
                      <Box>
                        <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "text.primary" }}>
                          {userData.FullName ?? userData.UserName}
                        </Typography>
                        {userData?.UserRoles?.length > 0 && (
                          <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", mt: 0.5 }}>
                            {getUserRole()}
                          </Typography>
                        )}
                        <Box
                          component="button"
                          onClick={() => setShowSignOutModal(true)}
                          sx={{
                            mt: 1.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            color: "#ff6b6b",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            p: 0,
                            "&:hover": { color: "#ff4040" },
                          }}
                        >
                          <Logout sx={{ fontSize: 14 }} />
                          <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                            {localize.tooltip_logout}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  >
                    <Box
                      component="button"
                      onClick={() => setShowUserProfile(true)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        p: 0,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 30,
                          height: 30,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          bgcolor: "rgba(255,255,255,0.15)",
                          color: "#fff",
                        }}
                      >
                        {(userData.UserName ?? "U").substring(0, 2).toUpperCase()}
                      </Avatar>
                      <Typography sx={{ fontSize: "0.78rem", fontWeight: 500, color: "white" }}>
                        {userData.UserName}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      <GeneralNotificationArea />

      <NotificationPane
        isOpen={showNotifications}
        setIsOpen={setShowNotifications}
        setNotificationCount={setNotificationCount}
        user={userData?.UserName}
        personalId={userData?.PesonalId}
      />
    </div>
  ) : (
    <div />
  );
}
